import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 * @property {HTMLElement} [zoomCursorBadge] - Floating "zoom in" label (desktop hover).
 * @property {HTMLElement} [zoomOutCursorBadge] - Floating "zoom out" label in zoom dialog.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#setupZoomCursorBadge();
  }

  #controller = new AbortController();

  /** @type {AbortController | null} */
  #zoomCursorListenersAbort = null;

  /** @type {MediaQueryList | null} */
  #zoomCursorMq = null;

  /** @type {(() => void) | null} */
  #zoomCursorMqListener = null;

  /** @type {number} */
  #zoomCursorRaf = 0;

  /** @type {{ clientX: number; clientY: number; badge: HTMLElement } | null} */
  #zoomCursorPending = null;

  static #ZOOM_IN_TARGET =
    '.product-media-container__zoom-button, .product-media-container--zoomable.product-media-container--image';

  static #ZOOM_OUT_TARGET =
    '.dialog-zoomed-gallery .product-media-container--zoomable.product-media-container--image';

  static #ZOOM_DIALOG_CHROME =
    'button, a[href], .dialog-zoomed-gallery__arrows, .dialog-thumbnails-list-container, .dialog-thumbnails-list';

  disconnectedCallback() {
    this.#teardownZoomCursorBadge();
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  #setupZoomCursorBadge() {
    if (this.dataset.zoomHoverLabel !== 'true') return;

    const zoomIn = this.refs.zoomCursorBadge;
    const zoomOut = this.refs.zoomOutCursorBadge;
    if (!(zoomIn instanceof HTMLElement) && !(zoomOut instanceof HTMLElement)) return;

    const dialog = this.refs.zoomDialogComponent?.querySelector('dialog');
    if (dialog instanceof HTMLDialogElement) {
      dialog.addEventListener('toggle', this.#onZoomDialogToggle);
    }

    const mq = window.matchMedia('(min-width: 750px) and (hover: hover)');
    this.#zoomCursorMq = mq;
    this.#zoomCursorMqListener = () => this.#syncZoomCursorPointerListeners();
    mq.addEventListener('change', this.#zoomCursorMqListener);
    this.#syncZoomCursorPointerListeners();
  }

  #teardownZoomCursorBadge() {
    this.#zoomCursorListenersAbort?.abort();
    this.#zoomCursorListenersAbort = null;

    if (this.#zoomCursorMq && this.#zoomCursorMqListener) {
      this.#zoomCursorMq.removeEventListener('change', this.#zoomCursorMqListener);
    }
    this.#zoomCursorMq = null;
    this.#zoomCursorMqListener = null;

    const dialog = this.refs.zoomDialogComponent?.querySelector('dialog');
    if (dialog instanceof HTMLDialogElement) {
      dialog.removeEventListener('toggle', this.#onZoomDialogToggle);
    }

    this.#stopZoomBadgeAnimation();

    this.#hideZoomInBadge();
    this.#hideZoomOutBadge();
  }

  #syncZoomCursorPointerListeners() {
    this.#zoomCursorListenersAbort?.abort();
    this.#zoomCursorListenersAbort = null;

    if (!this.#zoomCursorMq?.matches) {
      this.#hideZoomInBadge();
      this.#hideZoomOutBadge();
      return;
    }

    const ac = new AbortController();
    this.#zoomCursorListenersAbort = ac;
    const { signal } = ac;

    this.addEventListener('pointermove', this.#onZoomCursorPointerMove, { signal });
    this.addEventListener('pointerleave', this.#onZoomCursorPointerLeave, { signal });
  }

  /** @param {Event} event */
  #onZoomDialogToggle = (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (dialog.open) {
      this.#hideZoomInBadge();
    } else {
      this.#hideZoomOutBadge();
    }
  };

  /** @param {PointerEvent} event */
  #onZoomCursorPointerMove = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const dialogEl = this.refs.zoomDialogComponent?.querySelector('dialog');
    const dialogOpen = dialogEl instanceof HTMLDialogElement && dialogEl.open;
    const zoomOutBadge = this.refs.zoomOutCursorBadge;

    if (!dialogOpen) {
      this.#hideZoomOutBadge();
    }

    if (dialogOpen && zoomOutBadge instanceof HTMLElement) {
      if (!target.closest(MediaGallery.#ZOOM_DIALOG_CHROME)) {
        const zoomOutZone = target.closest(MediaGallery.#ZOOM_OUT_TARGET);
        if (zoomOutZone && this.contains(zoomOutZone)) {
          // Check if the drag-zoom-wrapper is already desktop-zoomed
          const dragZoomWrapper = zoomOutZone.querySelector('drag-zoom-wrapper') || zoomOutZone.closest('drag-zoom-wrapper');
          const isDesktopZoomed = dragZoomWrapper?.dataset.desktopZoomed === 'true';

          // Update the badge label/icon to reflect zoom state
          const iconEl = zoomOutBadge.querySelector('.product-gallery-zoom-cursor-badge__icon');
          const labelEl = zoomOutBadge.querySelector('.product-gallery-zoom-cursor-badge__label');
          const zoomInBadge = this.refs.zoomCursorBadge;

          if (isDesktopZoomed) {
            if (iconEl) iconEl.textContent = '−';
            if (labelEl && zoomInBadge) {
              const originalLabel = zoomInBadge.nextElementSibling?.textContent || labelEl.dataset.zoomOutLabel || labelEl.textContent;
              if (!labelEl.dataset.zoomOutLabel) labelEl.dataset.zoomOutLabel = labelEl.textContent.trim();
              labelEl.textContent = labelEl.dataset.zoomOutLabel;
            }
          } else {
            if (iconEl) iconEl.textContent = '+';
            if (labelEl && zoomInBadge) {
              const zoomInLabel = zoomInBadge.querySelector('.product-gallery-zoom-cursor-badge__label');
              if (!labelEl.dataset.zoomOutLabel) labelEl.dataset.zoomOutLabel = labelEl.textContent.trim();
              if (zoomInLabel) labelEl.textContent = zoomInLabel.textContent;
            }
          }

          this.#hideZoomInBadge();
          this.#scheduleBadgePosition(zoomOutBadge, event.clientX, event.clientY);
          return;
        }
      }
      this.#hideZoomOutBadge();
    }

    const zone = target.closest(MediaGallery.#ZOOM_IN_TARGET);
    if (!zone || !this.contains(zone) || zone.closest('.dialog-zoomed-gallery')) {
      this.#hideZoomInBadge();
      return;
    }

    const zoomInBadge = this.refs.zoomCursorBadge;
    if (!(zoomInBadge instanceof HTMLElement)) return;

    this.#hideZoomOutBadge();
    this.#scheduleBadgePosition(zoomInBadge, event.clientX, event.clientY);
  };

  #onZoomCursorPointerLeave = () => {
    this.#hideZoomInBadge();
    this.#hideZoomOutBadge();
  };

  #stopZoomBadgeAnimation() {
    if (this.#zoomCursorRaf) {
      cancelAnimationFrame(this.#zoomCursorRaf);
      this.#zoomCursorRaf = 0;
    }
    this.#zoomCursorPending = null;
  }

  /**
   * @param {HTMLElement} badge
   * @param {number} clientX
   * @param {number} clientY
   */
  #scheduleBadgePosition(badge, clientX, clientY) {
    if (this.#zoomCursorPending && this.#zoomCursorPending.badge !== badge) {
      this.#stopZoomBadgeAnimation();
    }

    this.#zoomCursorPending = { clientX, clientY, badge };

    if (this.#zoomCursorRaf) return;

    this.#zoomCursorRaf = requestAnimationFrame(() => {
      this.#zoomCursorRaf = 0;
      const pending = this.#zoomCursorPending;
      if (!pending) return;

      const { badge: activeBadge, clientX: x0, clientY: y0 } = pending;

      const offset = 14;
      let x = x0 + offset;
      let y = y0 + offset;

      activeBadge.classList.add('is-visible');

      const w = activeBadge.offsetWidth;
      const h = activeBadge.offsetHeight;
      const pad = 8;
      const maxX = window.innerWidth - w - pad;
      const maxY = window.innerHeight - h - pad;
      x = Math.min(Math.max(pad, x), maxX);
      y = Math.min(Math.max(pad, y), maxY);

      activeBadge.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    });
  }

  #hideZoomInBadge() {
    const badge = this.refs.zoomCursorBadge;
    if (badge instanceof HTMLElement) {
      if (this.#zoomCursorPending?.badge === badge) {
        this.#stopZoomBadgeAnimation();
      }
      badge.classList.remove('is-visible');
    }
  }

  #hideZoomOutBadge() {
    const badge = this.refs.zoomOutCursorBadge;
    if (badge instanceof HTMLElement) {
      if (this.#zoomCursorPending?.badge === badge) {
        this.#stopZoomBadgeAnimation();
      }
      badge.classList.remove('is-visible');
    }
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.#hideZoomInBadge();
    this.#hideZoomOutBadge();
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
